/**
 * Planning Scenario Widget Component
 * Displays an overview of AI Planning Scenarios with status summary
 * and recent scenarios list
 */

import React, { useState } from 'react';
import {
  Brain,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Calendar,
  RefreshCw,
  ChevronRight,
  Zap,
  Upload,
} from 'lucide-react';
import clsx from 'clsx';
import {
  PlanningScenario,
  ScenarioSummary,
  ScenarioStatus,
  getScenarioStatusInfo,
} from '../../hooks/usePlanningScenarios';

export interface PlanningScenarioWidgetProps {
  summary?: ScenarioSummary;
  loading?: boolean;
  onRefresh?: () => void;
  onCalculate?: (scenarioId: string) => Promise<{ job_id: string }>;
  onViewScenario?: (scenarioId: string) => void;
  isCalculating?: boolean;
}

// Status icon mapping
const statusIcons: Record<ScenarioStatus, React.ComponentType<{ className?: string }>> = {
  draft: FileText,
  calculating: Loader2,
  pending_review: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  applied: Upload,
  failed: AlertTriangle,
};

export function PlanningScenarioWidget({
  summary,
  loading,
  onRefresh,
  onCalculate,
  onViewScenario,
  isCalculating,
}: PlanningScenarioWidgetProps) {
  const [calculatingId, setCalculatingId] = useState<string | null>(null);

  // Handle calculate click
  const handleCalculate = async (scenarioId: string) => {
    if (!onCalculate || calculatingId) return;
    setCalculatingId(scenarioId);
    try {
      await onCalculate(scenarioId);
    } finally {
      setCalculatingId(null);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB');
  };

  // Format date range
  const formatDateRange = (start: string, end: string): string => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
    return `${startDate.toLocaleDateString('en-GB', options)} - ${endDate.toLocaleDateString('en-GB', options)}`;
  };

  // Format percentage
  const formatPercent = (value: number | null): string => {
    if (value === null) return '-';
    return `${(value * 100).toFixed(0)}%`;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 w-40 bg-gray-200 rounded" />
            <div className="h-8 w-24 bg-gray-200 rounded" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-lg" />
            ))}
          </div>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const defaultByStatus: Record<ScenarioStatus, number> = {
    draft: 0,
    calculating: 0,
    pending_review: 0,
    approved: 0,
    rejected: 0,
    applied: 0,
    failed: 0,
  };
  const byStatus = summary?.by_status || defaultByStatus;
  const total = summary?.total || 0;
  const recent = summary?.recent || [];

  // Status summary cards config
  const statusCards: {
    key: ScenarioStatus;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bgColor: string;
  }[] = [
    { key: 'pending_review', label: 'Pending Review', icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    { key: 'calculating', label: 'Calculating', icon: Loader2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50' },
    { key: 'applied', label: 'Applied', icon: Upload, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#45bdd6]" />
          <span className="font-medium text-gray-700">AI Planning Scenarios</span>
          <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
            {total} total
          </span>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 rounded hover:bg-gray-200 text-gray-500 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {statusCards.map(({ key, label, icon: Icon, color, bgColor }) => (
            <div
              key={key}
              className={clsx(
                'rounded-lg p-3 border',
                bgColor,
                'border-gray-200'
              )}
            >
              <div className="flex items-center justify-between">
                <Icon className={clsx('w-5 h-5', color, key === 'calculating' && 'animate-spin')} />
                <span className={clsx('text-2xl font-bold', color)}>
                  {byStatus[key] || 0}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Recent Scenarios List */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Recent Scenarios</div>
          <div className="space-y-2">
            {recent.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Brain className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm">No scenarios yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Create a scenario in the Admin Panel
                </p>
              </div>
            ) : (
              recent.map((scenario) => {
                const statusInfo = getScenarioStatusInfo(scenario.status);
                const StatusIcon = statusIcons[scenario.status] || FileText;
                const canCalculate = scenario.status === 'draft' || scenario.status === 'rejected';
                const isThisCalculating = calculatingId === scenario.id;

                return (
                  <div
                    key={scenario.id}
                    className={clsx(
                      'flex items-center p-3 rounded-lg border border-gray-200 bg-white',
                      'hover:bg-gray-50 transition-colors',
                      onViewScenario && 'cursor-pointer'
                    )}
                    onClick={() => onViewScenario?.(scenario.id)}
                  >
                    {/* Status Icon */}
                    <div className={clsx('p-2 rounded-lg', statusInfo.bgColor)}>
                      <StatusIcon
                        className={clsx(
                          'w-4 h-4',
                          statusInfo.color,
                          scenario.status === 'calculating' && 'animate-spin'
                        )}
                      />
                    </div>

                    {/* Scenario Info */}
                    <div className="flex-1 ml-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {scenario.name}
                        </span>
                        <span
                          className={clsx(
                            'text-xs px-2 py-0.5 rounded-full',
                            statusInfo.bgColor,
                            statusInfo.color
                          )}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDateRange(scenario.planning_start, scenario.planning_end)}
                        </span>
                        <span>{formatRelativeTime(scenario.updated_at)}</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="hidden md:flex items-center gap-4 mr-4">
                      {scenario.on_time_rate !== null && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {formatPercent(scenario.on_time_rate)}
                          </div>
                          <div className="text-xs text-gray-500">On-Time</div>
                        </div>
                      )}
                      {scenario.total_operations > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {scenario.total_operations}
                          </div>
                          <div className="text-xs text-gray-500">Ops</div>
                        </div>
                      )}
                      {scenario.total_conflicts > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-red-600">
                            {scenario.total_conflicts}
                          </div>
                          <div className="text-xs text-gray-500">Conflicts</div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {canCalculate && onCalculate && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCalculate(scenario.id);
                          }}
                          disabled={isThisCalculating || isCalculating}
                          className={clsx(
                            'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors',
                            isThisCalculating
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-[#45bdd6] text-white hover:bg-[#3ba8bf]'
                          )}
                          title="Run AI Calculation"
                        >
                          {isThisCalculating ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Zap className="w-3 h-3" />
                          )}
                          <span className="hidden sm:inline">Calculate</span>
                        </button>
                      )}
                      {onViewScenario && (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Stats Footer */}
        {total > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>
                <strong className="text-gray-700">{byStatus.draft || 0}</strong> Draft
              </span>
              <span>
                <strong className="text-gray-700">{byStatus.rejected || 0}</strong> Rejected
              </span>
              <span>
                <strong className="text-red-600">{byStatus.failed || 0}</strong> Failed
              </span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span>Auto-refresh: 30s</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlanningScenarioWidget;
